import { useCallback, useEffect, useState } from 'react';
import {
  UserCheck,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  ShieldAlert,
  CheckCircle2,
  XCircle,
  Eye,
  Mail,
  MailCheck,
} from 'lucide-react';
import { Layout } from '../components/layout/Layout';
import { Card, CardHeader } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Modal } from '../components/ui/Modal';
import { Badge } from '../components/ui/Badge';
import { Table, TableHead, TableBody, TableRow, TableHeader, TableCell } from '../components/ui/Table';
import { useToast } from '../components/ui/Toast';
import { useAuth } from '../context/AuthContext';
import { api } from '../services/api';
import { formatDateTime, truncateText } from '../utils/helpers';
import type {
  BlogPressAccessRequest,
  BlogPressAccessRequestStatus,
  BlogPressContributorStatus,
} from '../types';

// Optional UI-only safeguard: a comma-separated allowlist of approver
// emails. Empty (the default) shows Approve/Reject to every full admin,
// matching exactly what the backend currently permits — see the banner
// below and the Part 3 handover notes for why this is NOT a security
// boundary: the backend's admin_required only checks is_admin, not a
// specific identity, so any admin can still call the approve/reject API
// directly regardless of what this hides in the UI.
const APPROVER_ALLOWLIST: string[] = (import.meta.env.VITE_BLOG_PRESS_APPROVER_EMAILS || '')
  .split(',')
  .map((email: string) => email.trim().toLowerCase())
  .filter(Boolean);

type RequestTab = Exclude<BlogPressAccessRequestStatus, 'revoked'> | 'contributors';

const REQUEST_TABS: { id: RequestTab; label: string }[] = [
  { id: 'pending', label: 'Pending' },
  { id: 'approved', label: 'Approved' },
  { id: 'rejected', label: 'Rejected' },
  { id: 'contributors', label: 'Contributors' },
];

const STATUS_BADGE_VARIANT: Record<BlogPressAccessRequestStatus, 'default' | 'success' | 'warning' | 'error' | 'info'> = {
  pending: 'warning',
  approved: 'success',
  rejected: 'error',
  revoked: 'default',
};

const PAGE_SIZE = 20;

type ActionKind = 'approve' | 'reject' | 'revoke';

interface ActionTarget {
  request: BlogPressAccessRequest;
  action: ActionKind;
}

export function BlogPressAccessRequests() {
  const { toast } = useToast();
  const { user } = useAuth();

  const [tab, setTab] = useState<RequestTab>('pending');
  const [contributorStatus, setContributorStatus] = useState<BlogPressContributorStatus>('approved');
  const [offset, setOffset] = useState(0);

  const [requests, setRequests] = useState<BlogPressAccessRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [detailRequest, setDetailRequest] = useState<BlogPressAccessRequest | null>(null);
  const [actionTarget, setActionTarget] = useState<ActionTarget | null>(null);
  const [rejectNote, setRejectNote] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  const fetchRequests = useCallback(
    async (isRefresh = false) => {
      if (isRefresh) setRefreshing(true);
      else setLoading(true);

      try {
        setError(null);
        const data =
          tab === 'contributors'
            ? await api.getBlogPressContributors({ status: contributorStatus, limit: PAGE_SIZE, offset })
            : await api.getBlogPressAccessRequests({ status: tab, limit: PAGE_SIZE, offset });
        setRequests(data);
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to load Blog/Press access requests';
        setError(message);
        if (isRefresh) toast(message, 'error');
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [tab, contributorStatus, offset, toast]
  );

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  const refetchSilently = useCallback(async () => {
    try {
      const data =
        tab === 'contributors'
          ? await api.getBlogPressContributors({ status: contributorStatus, limit: PAGE_SIZE, offset })
          : await api.getBlogPressAccessRequests({ status: tab, limit: PAGE_SIZE, offset });
      setRequests(data);
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Failed to refresh', 'error');
    }
  }, [tab, contributorStatus, offset, toast]);

  const switchTab = (next: RequestTab) => {
    setTab(next);
    setOffset(0);
  };

  const switchContributorStatus = (next: BlogPressContributorStatus) => {
    setContributorStatus(next);
    setOffset(0);
  };

  const currentEmail = (user?.email || '').toLowerCase();
  const isDesignatedApprover = APPROVER_ALLOWLIST.length === 0 || APPROVER_ALLOWLIST.includes(currentEmail);

  const openApprove = (request: BlogPressAccessRequest) => setActionTarget({ request, action: 'approve' });
  const openReject = (request: BlogPressAccessRequest) => {
    setRejectNote('');
    setActionTarget({ request, action: 'reject' });
  };
  const openRevoke = (request: BlogPressAccessRequest) => setActionTarget({ request, action: 'revoke' });

  const runAction = async () => {
    if (!actionTarget) return;
    const { request, action } = actionTarget;

    setActionLoading(true);
    try {
      if (action === 'approve') {
        await api.approveBlogPressAccessRequest(request.id);
        toast(`Approved ${request.full_name || request.email}.`, 'success');
      } else if (action === 'reject') {
        await api.rejectBlogPressAccessRequest(request.id, rejectNote);
        toast(`Rejected ${request.full_name || request.email}.`, 'success');
      } else {
        await api.revokeBlogPressContributor(request.id);
        toast(`Revoked Blog/Press access for ${request.full_name || request.email}.`, 'success');
      }
      setActionTarget(null);
      setRejectNote('');
      await refetchSilently();
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Action failed. Please try again.', 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const showingContributors = tab === 'contributors';

  return (
    <Layout>
      <div className="space-y-6 animate-fade-in">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-[#f8fafc] flex items-center gap-2">
              <UserCheck className="w-6 h-6 text-[#3b82f6]" />
              Blog/Press Access Requests
            </h1>
            <p className="mt-1 text-sm text-[#94a3b8]">
              Review contributor access requests and manage who can manage Blog/Press content.
            </p>
          </div>
          <Button
            variant="secondary"
            icon={<RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />}
            onClick={() => fetchRequests(true)}
            disabled={refreshing}
          >
            Refresh
          </Button>
        </div>

        <div className="rounded-xl border border-amber-500/20 bg-amber-500/5 px-4 py-3 flex items-start gap-2.5">
          <ShieldAlert className="w-4 h-4 shrink-0 mt-0.5 text-[#f59e0b]" />
          <p className="text-xs text-[#f59e0b] leading-relaxed">
            The backend currently allows <strong>any</strong> administrator to approve or reject
            requests — it is not yet restricted to a specific approver.{' '}
            {APPROVER_ALLOWLIST.length > 0
              ? 'Approve/Reject is hidden below for accounts not on VITE_BLOG_PRESS_APPROVER_EMAILS as a UI convenience only — this is not enforced security.'
              : 'Set VITE_BLOG_PRESS_APPROVER_EMAILS to additionally hide these actions here for other admins — doing so is still a UI convenience only, not enforced security, until the backend restricts approval itself.'}
          </p>
        </div>

        <Card padding="none">
          <div className="p-6 pb-0">
            <CardHeader title="Requests" />
            <div className="flex flex-wrap gap-2 mb-5">
              {REQUEST_TABS.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => switchTab(t.id)}
                  aria-pressed={tab === t.id}
                  className={`px-3.5 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
                    tab === t.id
                      ? 'bg-[#3b82f6]/15 border-[#3b82f6] text-[#3b82f6]'
                      : 'border-[#252535] bg-[#1c1c2a] text-[#94a3b8] hover:text-[#f1f5f9] hover:border-[#353548]'
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>
            {showingContributors && (
              <div className="flex gap-2 mb-5">
                {(['approved', 'revoked'] as const).map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => switchContributorStatus(s)}
                    aria-pressed={contributorStatus === s}
                    className={`px-3 py-1 rounded-full text-[11px] font-semibold border transition-colors ${
                      contributorStatus === s
                        ? 'bg-[#1c1c2a] border-[#353548] text-[#f1f5f9]'
                        : 'border-transparent text-[#64748b] hover:text-[#94a3b8]'
                    }`}
                  >
                    {s === 'approved' ? 'Active' : 'Revoked'}
                  </button>
                ))}
              </div>
            )}
          </div>

          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin w-8 h-8 border-2 border-[#3b82f6] border-t-transparent rounded-full" />
            </div>
          ) : error && requests.length === 0 ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <div className="w-12 h-12 bg-red-500/10 rounded-2xl flex items-center justify-center mx-auto mb-3">
                  <span className="text-[#ef4444] text-xl">!</span>
                </div>
                <p className="text-[#ef4444] font-medium">Failed to load</p>
                <p className="text-[#64748b] text-sm mt-1">{error}</p>
                <button
                  onClick={() => fetchRequests()}
                  className="mt-4 px-4 py-2 text-sm rounded-xl bg-[#141420] border border-[#252535] text-[#94a3b8] hover:text-[#f1f5f9] hover:border-[#353548] transition-colors"
                >
                  Retry
                </button>
              </div>
            </div>
          ) : requests.length === 0 ? (
            <p className="text-sm text-[#64748b] py-12 text-center">
              {showingContributors
                ? contributorStatus === 'approved'
                  ? 'No active contributors yet.'
                  : 'No revoked contributors.'
                : `No ${tab} requests.`}
            </p>
          ) : (
            <div className="px-6 pb-4">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableHeader>Name</TableHeader>
                    <TableHeader>Email</TableHeader>
                    {!showingContributors && <TableHeader>Email Verified</TableHeader>}
                    {showingContributors && <TableHeader>Linked Account</TableHeader>}
                    <TableHeader>{showingContributors ? 'Approved' : 'Submitted'}</TableHeader>
                    <TableHeader>Status</TableHeader>
                    <TableHeader className="text-right">Actions</TableHeader>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {requests.map((request) => (
                    <TableRow key={request.id}>
                      <TableCell>
                        <p className="font-medium text-[#f1f5f9]">{request.full_name || '-'}</p>
                        <p className="text-xs text-[#64748b] max-w-xs truncate">
                          {truncateText(request.reason || '', 60)}
                        </p>
                      </TableCell>
                      <TableCell className="text-[#94a3b8]">{request.email}</TableCell>
                      {!showingContributors && (
                        <TableCell>
                          {request.email_verified ? (
                            <span className="inline-flex items-center gap-1 text-xs text-[#22c55e]">
                              <MailCheck className="w-3.5 h-3.5" /> Verified
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-xs text-[#64748b]">
                              <Mail className="w-3.5 h-3.5" /> Unverified
                            </span>
                          )}
                        </TableCell>
                      )}
                      {showingContributors && (
                        <TableCell className="text-[#94a3b8] text-xs">
                          {request.contributor_uid ? 'Linked' : 'Not yet linked'}
                        </TableCell>
                      )}
                      <TableCell className="text-[#94a3b8] text-xs">
                        {formatDateTime(
                          (showingContributors ? request.reviewed_at : request.created_at) || undefined
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge variant={STATUS_BADGE_VARIANT[request.status]}>{request.status}</Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            icon={<Eye className="w-3.5 h-3.5" />}
                            onClick={() => setDetailRequest(request)}
                          >
                            View
                          </Button>
                          {tab === 'pending' && isDesignatedApprover && (
                            <>
                              <Button
                                variant="secondary"
                                size="sm"
                                icon={<CheckCircle2 className="w-3.5 h-3.5" />}
                                onClick={() => openApprove(request)}
                              >
                                Approve
                              </Button>
                              <Button
                                variant="danger"
                                size="sm"
                                icon={<XCircle className="w-3.5 h-3.5" />}
                                onClick={() => openReject(request)}
                              >
                                Reject
                              </Button>
                            </>
                          )}
                          {showingContributors && contributorStatus === 'approved' && isDesignatedApprover && (
                            <Button variant="danger" size="sm" onClick={() => openRevoke(request)}>
                              Revoke
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              <div className="flex items-center justify-between pt-4">
                <p className="text-xs text-[#64748b]">
                  Showing {offset + 1}–{offset + requests.length}
                </p>
                <div className="flex items-center gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setOffset((o) => Math.max(0, o - PAGE_SIZE))}
                    disabled={offset === 0}
                    icon={<ChevronLeft className="w-4 h-4" />}
                  >
                    Previous
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setOffset((o) => o + PAGE_SIZE)}
                    disabled={requests.length < PAGE_SIZE}
                    icon={<ChevronRight className="w-4 h-4" />}
                  >
                    Next
                  </Button>
                </div>
              </div>
            </div>
          )}
        </Card>
      </div>

      {/* Detail modal */}
      <Modal
        isOpen={detailRequest !== null}
        onClose={() => setDetailRequest(null)}
        title="Request details"
        size="md"
        footer={
          <Button variant="secondary" onClick={() => setDetailRequest(null)}>
            Close
          </Button>
        }
      >
        {detailRequest && (
          <div className="space-y-4">
            <div>
              <p className="text-xs font-semibold text-[#64748b] uppercase tracking-wider mb-1">Full Name</p>
              <p className="text-sm text-[#f1f5f9]">{detailRequest.full_name || '-'}</p>
            </div>
            <div>
              <p className="text-xs font-semibold text-[#64748b] uppercase tracking-wider mb-1">Email</p>
              <p className="text-sm text-[#f1f5f9]">{detailRequest.email}</p>
            </div>
            <div>
              <p className="text-xs font-semibold text-[#64748b] uppercase tracking-wider mb-1">Reason</p>
              <p className="text-sm text-[#f1f5f9] whitespace-pre-wrap">{detailRequest.reason || '-'}</p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-xs font-semibold text-[#64748b] uppercase tracking-wider mb-1">
                  Email Verified
                </p>
                <p className="text-sm text-[#f1f5f9]">
                  {detailRequest.email_verified ? `Yes — ${formatDateTime(detailRequest.verified_at || undefined)}` : 'No'}
                </p>
              </div>
              <div>
                <p className="text-xs font-semibold text-[#64748b] uppercase tracking-wider mb-1">Status</p>
                <Badge variant={STATUS_BADGE_VARIANT[detailRequest.status]}>{detailRequest.status}</Badge>
              </div>
              <div>
                <p className="text-xs font-semibold text-[#64748b] uppercase tracking-wider mb-1">Submitted</p>
                <p className="text-sm text-[#f1f5f9]">{formatDateTime(detailRequest.created_at || undefined)}</p>
              </div>
              <div>
                <p className="text-xs font-semibold text-[#64748b] uppercase tracking-wider mb-1">
                  Linked Account
                </p>
                <p className="text-sm text-[#f1f5f9]">
                  {detailRequest.contributor_uid
                    ? `Linked — ${formatDateTime(detailRequest.linked_at || undefined)}`
                    : 'Not yet linked'}
                </p>
              </div>
              {detailRequest.reviewed_by && (
                <>
                  <div>
                    <p className="text-xs font-semibold text-[#64748b] uppercase tracking-wider mb-1">
                      Reviewed By
                    </p>
                    <p className="text-sm text-[#f1f5f9] break-all">{detailRequest.reviewed_by}</p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-[#64748b] uppercase tracking-wider mb-1">
                      Reviewed At
                    </p>
                    <p className="text-sm text-[#f1f5f9]">{formatDateTime(detailRequest.reviewed_at || undefined)}</p>
                  </div>
                </>
              )}
              {detailRequest.review_note && (
                <div className="col-span-2">
                  <p className="text-xs font-semibold text-[#64748b] uppercase tracking-wider mb-1">
                    Reviewer Note
                  </p>
                  <p className="text-sm text-[#f1f5f9]">{detailRequest.review_note}</p>
                </div>
              )}
              {detailRequest.revoked_by && (
                <>
                  <div>
                    <p className="text-xs font-semibold text-[#64748b] uppercase tracking-wider mb-1">
                      Revoked By
                    </p>
                    <p className="text-sm text-[#f1f5f9] break-all">{detailRequest.revoked_by}</p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-[#64748b] uppercase tracking-wider mb-1">
                      Revoked At
                    </p>
                    <p className="text-sm text-[#f1f5f9]">{formatDateTime(detailRequest.revoked_at || undefined)}</p>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </Modal>

      {/* Approve / Reject / Revoke confirmation */}
      <Modal
        isOpen={actionTarget !== null}
        onClose={() => {
          if (!actionLoading) {
            setActionTarget(null);
            setRejectNote('');
          }
        }}
        title={
          actionTarget?.action === 'approve'
            ? 'Approve request'
            : actionTarget?.action === 'reject'
              ? 'Reject request'
              : 'Revoke contributor access'
        }
        size="sm"
        allowDismiss={!actionLoading}
        footer={
          <>
            <Button
              variant="secondary"
              onClick={() => {
                setActionTarget(null);
                setRejectNote('');
              }}
              disabled={actionLoading}
            >
              Cancel
            </Button>
            <Button
              variant={actionTarget?.action === 'approve' ? 'primary' : 'danger'}
              onClick={runAction}
              loading={actionLoading}
            >
              {actionTarget?.action === 'approve'
                ? 'Approve'
                : actionTarget?.action === 'reject'
                  ? 'Reject'
                  : 'Revoke'}
            </Button>
          </>
        }
      >
        {actionTarget && (
          <div className="space-y-3">
            <p className="text-[#94a3b8] text-sm">
              {actionTarget.action === 'approve' && (
                <>
                  Approve contributor access for{' '}
                  <span className="text-[#f8fafc] font-medium">
                    {actionTarget.request.full_name || actionTarget.request.email}
                  </span>
                  ? They&apos;ll be able to manage Blog/Press content once they sign in with this verified
                  email.
                </>
              )}
              {actionTarget.action === 'reject' && (
                <>
                  Reject the request from{' '}
                  <span className="text-[#f8fafc] font-medium">
                    {actionTarget.request.full_name || actionTarget.request.email}
                  </span>
                  ? They can submit a new request later.
                </>
              )}
              {actionTarget.action === 'revoke' && (
                <>
                  Revoke Blog/Press access for{' '}
                  <span className="text-[#f8fafc] font-medium">
                    {actionTarget.request.full_name || actionTarget.request.email}
                  </span>
                  ? They&apos;ll immediately lose the ability to manage Blog/Press content. Their account and
                  any articles they created are not affected.
                </>
              )}
            </p>
            {actionTarget.action === 'reject' && (
              <div>
                <label className="block text-xs font-semibold text-[#64748b] uppercase tracking-wider mb-1.5">
                  Note (optional)
                </label>
                <textarea
                  rows={3}
                  value={rejectNote}
                  onChange={(e) => setRejectNote(e.target.value)}
                  className="w-full px-3.5 py-2.5 bg-[#1c1c2a] border border-[#252535] rounded-xl text-sm text-[#f1f5f9] placeholder-[#64748b] focus:outline-none focus:border-[#3b82f6] transition-colors"
                  placeholder="Shared with no one automatically — for your own records."
                />
              </div>
            )}
          </div>
        )}
      </Modal>
    </Layout>
  );
}
